import { useQuery } from "@tanstack/react-query";
import { useRouter } from "rhoto-router";
import { parquetReadObjects } from "hyparquet";
import { compressors } from "hyparquet-compressors";
import camelcaseKeys from "camelcase-keys";
import Point from "./point";

export const apiFetch = async (path: string) => {
  const url = `/api${path}`;
  const result = await fetch(url);
  if (result.ok) {
    const resultBody = await result.text();
    return JSON.parse(resultBody);
  } else throw result;
};

export function useAPIData<T>(route: string | null): T | null {
  const router = useRouter();

  const { data, error } = useQuery<T>({
    queryKey: ["api", route],
    queryFn: () => apiFetch(route!),
    enabled: route !== null,
    // Automatically deduplicated by TanStack Query
    // Cache configured globally in App.tsx
  });

  // Handle errors by redirecting to 404
  if (error) {
    router.notFound();
  }

  return data ?? null;
}

async function fetchParquet(userId: string, activityId: string) {
  const url = `/api/activity/${userId}/${activityId}.parquet`;
  const response = await fetch(url);
  if (response.ok) {
    const parquet = await response.arrayBuffer();
    const data = await parquetReadObjects({ file: parquet, compressors });
    return camelcaseKeys(data) as Point[];
  } else throw response;
}

export function useParquet(
  userId: string | null | undefined,
  activityId: string | null | undefined,
): Point[] | null {
  const router = useRouter();

  const { data, error } = useQuery<Point[]>({
    queryKey: ["parquet", activityId],
    queryFn: () => fetchParquet(userId!, activityId!),
    enabled: activityId !== null,
  });

  // Handle errors by redirecting to 404
  if (error) {
    router.notFound();
  }

  return data ?? null;
}
